import smartSearchDictionary from '../data/smartSearch/smart_search_dictionary_v1_updated.json';
import type { ServiceCapabilityIntent, ServiceLocationInfo } from '../types/firestore';

export type SmartSearchSuggestionType =
  | 'issue'
  | 'service'
  | 'diagnostic'
  | 'object'
  | 'category'
  | 'provider'
  | 'reportMissing';

export type SmartSearchMatchType = 'entry' | 'object' | 'category' | 'provider' | 'reportMissing';

export interface SmartSearchSelection {
  id: string;
  type: SmartSearchSuggestionType;
  matchType: SmartSearchMatchType;
  suggestionTitle: string;
  suggestionMeta: string;
  description?: string;
  inputSelectedLabel: string;
  intent?: ServiceCapabilityIntent;
  domainId?: string;
  categoryId?: string;
  objectId?: string;
  linkedCapabilityIds?: string[];
  providerId?: string;
}

interface DictionaryVisibilityFields {
  clientSearchable?: unknown;
  providerSelectable?: unknown;
  publicVisibleInV1?: unknown;
  adminOnly?: unknown;
  futureCandidate?: unknown;
}

interface DictionarySearchEntry extends DictionaryVisibilityFields {
  id?: unknown;
  domainId?: unknown;
  categoryId?: unknown;
  objectId?: unknown;
  intent?: unknown;
  canonicalLabel?: unknown;
  suggestionTitle?: unknown;
  suggestionMeta?: unknown;
  searchAliases?: unknown;
  linkedCapabilityIds?: unknown;
  fallbackEntryIds?: unknown;
  fallbackObjectId?: unknown;
  fallbackCategoryId?: unknown;
  symptoms?: unknown;
  inputSelectedLabel?: unknown;
  specificity?: unknown;
  fallbackLevel?: unknown;
  commonness?: unknown;
  matchWeight?: unknown;
}

interface DictionaryObject extends DictionaryVisibilityFields {
  id?: unknown;
  domainId?: unknown;
  categoryId?: unknown;
  label?: unknown;
  pluralLabel?: unknown;
  aliases?: unknown;
  priority?: unknown;
}

interface DictionaryCategory extends DictionaryVisibilityFields {
  id?: unknown;
  domainId?: unknown;
  label?: unknown;
  aliases?: unknown;
}

interface DictionaryDomain extends DictionaryVisibilityFields {
  id?: unknown;
  label?: unknown;
  aliases?: unknown;
}

interface DictionaryFallbackRule {
  preferredEntryId?: unknown;
  fallbackEntryId?: unknown;
  fallbackObjectId?: unknown;
  fallbackCategoryId?: unknown;
  fromAliasPattern?: unknown;
}

interface DictionaryIntentKeyword {
  normalized?: unknown;
  keyword?: unknown;
  intentSignal?: unknown;
  doesNotMatchEntryAlone?: unknown;
}

interface SmartSearchDictionaryShape {
  globalIntentKeywords?: DictionaryIntentKeyword[];
  searchEntries?: DictionarySearchEntry[];
  objects?: DictionaryObject[];
  categories?: DictionaryCategory[];
  domains?: DictionaryDomain[];
  fallbackRules?: DictionaryFallbackRule[];
}

type ScoredEntry = {
  selection: SmartSearchSelection;
  score: number;
  fallbackEntryIds: string[];
  fallbackObjectId?: string;
  fallbackCategoryId?: string;
};

type EntrySearchIndex = {
  normalizedTexts: string[];
  allEntryTokens: Set<string>;
  titleTokens: Set<string>;
  inputLabelTokens: Set<string>;
  aliasTokens: Set<string>;
  objectTokens: Set<string>;
  categoryTokens: Set<string>;
  domainTokens: Set<string>;
};

type RecognizedContext = {
  object?: PublicSmartSearchObject;
  category?: PublicSmartSearchCategory;
  intent?: ServiceCapabilityIntent;
};

type PublicSmartSearchObject = {
  id: string;
  domainId: string;
  categoryId: string;
  label: string;
  pluralLabel?: string;
  aliases: string[];
  priority?: string;
};

type PublicSmartSearchCategory = {
  id: string;
  domainId: string;
  label: string;
  aliases: string[];
};

const dictionary = smartSearchDictionary as SmartSearchDictionaryShape;
const SUGGESTION_LIMIT = 8;
const DIAGNOSTIC_INTENT_ONLY_OBJECT_ORDER = [
  'object.samochod',
  'object.laptop',
  'object.telefon',
  'object.pralka',
  'object.komputer_stacjonarny',
  'object.ekspres_do_kawy',
];

const INTENT_LABELS: Record<ServiceCapabilityIntent, string> = {
  N: 'Naprawa',
  D: 'Diagnostyka',
  U: 'Usługa',
};

const normalizeText = (value: unknown): string =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[łŁ]/g, 'l')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

export const normalizeSmartSearchText = normalizeText;

const toText = (value: unknown): string => String(value || '').trim();

const toStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map((item) => toText(item))
        .filter((item, index, source) => item && source.indexOf(item) === index)
    : [];

const isIntent = (value: unknown): value is ServiceCapabilityIntent =>
  value === 'N' || value === 'U' || value === 'D';

const isPublicClientVisible = (entry: DictionaryVisibilityFields): boolean =>
  entry.clientSearchable === true &&
  entry.publicVisibleInV1 === true &&
  entry.adminOnly !== true &&
  entry.futureCandidate !== true;

const tokenize = (value: string): string[] => {
  const normalized = normalizeText(value);
  if (!normalized) return [];
  return Array.from(new Set(normalized.match(/[a-z0-9]+/g) || []));
};

const tokenMatches = (queryToken: string, candidateToken: string, allowPrefix: boolean): boolean =>
  queryToken === candidateToken || (allowPrefix && candidateToken.startsWith(queryToken));

const findMatchingCandidateToken = (
  queryToken: string,
  candidateTokens: string[] | Set<string>,
  allowPrefix: boolean
): string | undefined => {
  let match: string | undefined;
  candidateTokens.forEach((candidateToken) => {
    if (match) return;
    if (tokenMatches(queryToken, candidateToken, allowPrefix)) {
      match = candidateToken;
    }
  });
  return match;
};

const globalIntentKeywords = (dictionary.globalIntentKeywords || [])
  .map((keyword) => ({
    normalized: normalizeText(keyword.normalized || keyword.keyword),
    intent: isIntent(keyword.intentSignal) ? keyword.intentSignal : undefined,
    signalOnly: keyword.doesNotMatchEntryAlone === true,
  }))
  .filter((keyword) => keyword.normalized);

const globalIntentTokens = new Set(
  globalIntentKeywords.flatMap((keyword) => tokenize(keyword.normalized))
);

const FALLBACK_INTENT_KEYWORDS: Array<{ normalized: string; intent: ServiceCapabilityIntent }> = [
  { normalized: 'diagnostyka', intent: 'D' },
  { normalized: 'diagnoza', intent: 'D' },
  { normalized: 'sprawdzenie', intent: 'D' },
  { normalized: 'naprawa', intent: 'N' },
  { normalized: 'nie dziala', intent: 'N' },
  { normalized: 'awaria', intent: 'N' },
  { normalized: 'zepsute', intent: 'N' },
  { normalized: 'instalacja', intent: 'U' },
  { normalized: 'montaz', intent: 'U' },
  { normalized: 'wymiana', intent: 'U' },
  { normalized: 'czyszczenie', intent: 'U' },
  { normalized: 'konfiguracja', intent: 'U' },
];

const intentKeywords = [
  ...globalIntentKeywords
    .filter((keyword): keyword is typeof keyword & { intent: ServiceCapabilityIntent } =>
      Boolean(keyword.intent)
    )
    .map((keyword) => ({
      normalized: keyword.normalized,
      intent: keyword.intent,
      tokens: tokenize(keyword.normalized),
    })),
  ...FALLBACK_INTENT_KEYWORDS.map((keyword) => ({
    normalized: keyword.normalized,
    intent: keyword.intent,
    tokens: tokenize(keyword.normalized),
  })),
].filter((keyword) => keyword.tokens.length > 0);

const publicCategories: PublicSmartSearchCategory[] = (dictionary.categories || [])
  .filter(isPublicClientVisible)
  .map((category) => ({
    id: toText(category.id),
    domainId: toText(category.domainId),
    label: toText(category.label),
    aliases: toStringList(category.aliases),
  }))
  .filter((category) => category.id && category.domainId && category.label);

const publicObjects: PublicSmartSearchObject[] = (dictionary.objects || [])
  .filter(isPublicClientVisible)
  .map((object) => ({
    id: toText(object.id),
    domainId: toText(object.domainId),
    categoryId: toText(object.categoryId),
    label: toText(object.label),
    pluralLabel: toText(object.pluralLabel) || undefined,
    aliases: toStringList(object.aliases),
    priority: toText(object.priority) || undefined,
  }))
  .filter((object) => object.id && object.domainId && object.categoryId && object.label);

const categoryById = new Map(publicCategories.map((category) => [category.id, category]));
const objectById = new Map(publicObjects.map((object) => [object.id, object]));
const domainById = new Map(
  (dictionary.domains || [])
    .filter(isPublicClientVisible)
    .map((domain): [string, { label: string; aliases: string[] }] => [
      toText(domain.id),
      {
        label: toText(domain.label),
        aliases: toStringList(domain.aliases),
      },
    ])
    .filter(([id, domain]) => id && domain.label)
);

const entryToSelection = (entry: DictionarySearchEntry): SmartSearchSelection | null => {
  const id = toText(entry.id);
  const domainId = toText(entry.domainId);
  const categoryId = toText(entry.categoryId);
  const objectId = toText(entry.objectId);
  const intent = isIntent(entry.intent) ? entry.intent : undefined;
  const suggestionTitle = toText(entry.suggestionTitle || entry.canonicalLabel);
  const suggestionMeta = toText(entry.suggestionMeta);
  const inputSelectedLabel = toText(
    entry.inputSelectedLabel || entry.canonicalLabel || entry.suggestionTitle
  );

  if (!id || !suggestionTitle || !inputSelectedLabel || !isPublicClientVisible(entry)) {
    return null;
  }

  return {
    id,
    type: intent === 'D' ? 'diagnostic' : intent === 'U' ? 'service' : 'issue',
    matchType: 'entry',
    suggestionTitle,
    suggestionMeta,
    inputSelectedLabel,
    intent,
    domainId: domainId || undefined,
    categoryId: categoryId || undefined,
    objectId: objectId || undefined,
    linkedCapabilityIds: toStringList(entry.linkedCapabilityIds),
  };
};

const publicSearchEntries = (dictionary.searchEntries || [])
  .map((entry) => ({
    raw: entry,
    selection: entryToSelection(entry),
  }))
  .filter((entry): entry is { raw: DictionarySearchEntry; selection: SmartSearchSelection } =>
    Boolean(entry.selection)
  );

const entryById = new Map(publicSearchEntries.map((entry) => [entry.selection.id, entry]));

const fallbackRules = (dictionary.fallbackRules || []).map((rule) => ({
  preferredEntryId: toText(rule.preferredEntryId),
  fallbackEntryId: toText(rule.fallbackEntryId),
  fallbackObjectId: toText(rule.fallbackObjectId),
  fallbackCategoryId: toText(rule.fallbackCategoryId),
  aliases: String(rule.fromAliasPattern || '')
    .split('/')
    .map((item) => normalizeText(item))
    .filter(Boolean),
}));

export const getPublicSmartSearchEntries = (): SmartSearchSelection[] =>
  publicSearchEntries.map((entry) => entry.selection);

export const getPublicSmartSearchObjects = (): PublicSmartSearchObject[] => publicObjects;

export const getPublicSmartSearchCategories = (): PublicSmartSearchCategory[] => publicCategories;

const detectIntent = (normalizedQuery: string): ServiceCapabilityIntent | undefined => {
  const queryTokens = tokenize(normalizedQuery);
  const matched = intentKeywords.find((keyword) => {
    if (normalizedQuery.includes(keyword.normalized)) return true;
    return keyword.tokens.every((keywordToken) =>
      queryTokens.some(
        (queryToken) =>
          queryToken === keywordToken ||
          (queryToken.length >= 3 && keywordToken.startsWith(queryToken))
      )
    );
  });
  return matched?.intent;
};

const isIntentQueryToken = (token: string): boolean =>
  globalIntentTokens.has(token) ||
  intentKeywords.some((keyword) =>
    keyword.tokens.some(
      (keywordToken) =>
        token === keywordToken || (token.length >= 3 && keywordToken.startsWith(token))
    )
  );

const getNonGenericTokens = (rawQuery: string): string[] =>
  tokenize(rawQuery).filter((token) => !isIntentQueryToken(token));

const isGlobalIntentOnlyQuery = (rawQuery: string): boolean => {
  const tokens = tokenize(rawQuery);
  if (tokens.length === 0) return false;
  return tokens.every(isIntentQueryToken);
};

const includesPhrase = (haystack: string, needle: string): boolean => {
  if (!haystack || !needle) return false;
  return ` ${haystack} `.includes(` ${needle} `) || haystack.includes(needle);
};

const getRecognizedObject = (normalizedQuery: string): PublicSmartSearchObject | undefined =>
  publicObjects
    .map((object) => {
      const aliases = [object.label, object.pluralLabel, ...object.aliases].filter(Boolean);
      const bestAlias = aliases
        .map((alias) => normalizeText(alias))
        .map((alias) => {
          if (includesPhrase(normalizedQuery, alias)) return { alias, score: alias.length + 100 };
          const aliasTokens = tokenize(alias);
          const queryTokens = tokenize(normalizedQuery).filter(
            (token) => !isIntentQueryToken(token)
          );
          const matches =
            queryTokens.length > 0 &&
            queryTokens.every((queryToken, index) =>
              Boolean(
                findMatchingCandidateToken(
                  queryToken,
                  aliasTokens,
                  index === queryTokens.length - 1 || queryToken.length >= 3
                )
              )
            );
          if (!matches) return null;
          const firstTokenStarts = aliasTokens[0]?.startsWith(queryTokens[0] || '') ? 30 : 0;
          return { alias, score: queryTokens.join('').length + firstTokenStarts };
        })
        .filter((match): match is { alias: string; score: number } => Boolean(match))
        .sort((a, b) => b.score - a.score || a.alias.length - b.alias.length)[0];
      return bestAlias
        ? { object, score: bestAlias.score + (object.priority === 'high' ? 10 : 0) }
        : null;
    })
    .filter((match): match is { object: PublicSmartSearchObject; score: number } => Boolean(match))
    .sort((a, b) => b.score - a.score || a.object.label.length - b.object.label.length)[0]?.object;

const getRecognizedCategory = (normalizedQuery: string): PublicSmartSearchCategory | undefined =>
  publicCategories
    .map((category) => {
      const aliases = [category.label, ...category.aliases].filter(Boolean);
      const bestAlias = aliases
        .map((alias) => normalizeText(alias))
        .map((alias) => {
          if (includesPhrase(normalizedQuery, alias)) return { alias, score: alias.length + 100 };
          const aliasTokens = tokenize(alias);
          const queryTokens = tokenize(normalizedQuery).filter(
            (token) => !isIntentQueryToken(token)
          );
          const matches =
            queryTokens.length > 0 &&
            queryTokens.every((queryToken, index) =>
              Boolean(
                findMatchingCandidateToken(
                  queryToken,
                  aliasTokens,
                  index === queryTokens.length - 1 || queryToken.length >= 3
                )
              )
            );
          if (!matches) return null;
          const firstTokenStarts = aliasTokens[0]?.startsWith(queryTokens[0] || '') ? 30 : 0;
          return { alias, score: queryTokens.join('').length + firstTokenStarts };
        })
        .filter((match): match is { alias: string; score: number } => Boolean(match))
        .sort((a, b) => b.score - a.score || a.alias.length - b.alias.length)[0];
      return bestAlias ? { category, score: bestAlias.score } : null;
    })
    .filter((match): match is { category: PublicSmartSearchCategory; score: number } =>
      Boolean(match)
    )
    .sort((a, b) => b.score - a.score)[0]?.category;

const getContext = (rawQuery: string): RecognizedContext => {
  const normalizedQuery = normalizeText(rawQuery);
  const object = getRecognizedObject(normalizedQuery);
  return {
    object,
    category: object ? categoryById.get(object.categoryId) : getRecognizedCategory(normalizedQuery),
    intent: detectIntent(normalizedQuery),
  };
};

const getEntrySearchTexts = (
  entry: DictionarySearchEntry,
  selection: SmartSearchSelection
): string[] => {
  const object = selection.objectId ? objectById.get(selection.objectId) : undefined;
  const category = selection.categoryId ? categoryById.get(selection.categoryId) : undefined;
  const domain = selection.domainId ? domainById.get(selection.domainId) : undefined;

  return [
    selection.suggestionTitle,
    selection.inputSelectedLabel,
    selection.suggestionMeta,
    toText(entry.canonicalLabel),
    object?.label || '',
    object?.pluralLabel || '',
    ...(object?.aliases || []),
    category?.label || '',
    ...(category?.aliases || []),
    domain?.label || '',
    ...(domain?.aliases || []),
    ...toStringList(entry.searchAliases),
    ...toStringList(entry.symptoms),
  ];
};

const entrySearchIndexById = new Map<string, EntrySearchIndex>();

const getEntrySearchIndex = (
  entry: DictionarySearchEntry,
  selection: SmartSearchSelection
): EntrySearchIndex => {
  const cached = entrySearchIndexById.get(selection.id);
  if (cached) return cached;

  const object = selection.objectId ? objectById.get(selection.objectId) : undefined;
  const category = selection.categoryId ? categoryById.get(selection.categoryId) : undefined;
  const domain = selection.domainId ? domainById.get(selection.domainId) : undefined;
  const normalizedTexts = getEntrySearchTexts(entry, selection).map(normalizeText).filter(Boolean);
  const index: EntrySearchIndex = {
    normalizedTexts,
    allEntryTokens: new Set(normalizedTexts.flatMap(tokenize)),
    titleTokens: new Set(tokenize(selection.suggestionTitle)),
    inputLabelTokens: new Set(tokenize(selection.inputSelectedLabel)),
    aliasTokens: new Set([
      ...toStringList(entry.searchAliases).flatMap(tokenize),
      ...toStringList(entry.symptoms).flatMap(tokenize),
    ]),
    objectTokens: new Set(
      [object?.label || '', object?.pluralLabel || '', ...(object?.aliases || [])].flatMap(tokenize)
    ),
    categoryTokens: new Set(
      [category?.label || '', ...(category?.aliases || [])].flatMap(tokenize)
    ),
    domainTokens: new Set([domain?.label || '', ...(domain?.aliases || [])].flatMap(tokenize)),
  };
  entrySearchIndexById.set(selection.id, index);
  return index;
};

const getCommonnessBoost = (value: unknown): number => {
  if (value === 'high') return 8;
  if (value === 'medium') return 4;
  return 0;
};

const getSpecificityBoost = (value: unknown): number => {
  if (value === 'specific') return 42;
  if (value === 'object') return 18;
  if (value === 'category') return 4;
  return 0;
};

const getFallbackPenalty = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed * 10 : 0;
};

const scoreEntry = (
  entry: DictionarySearchEntry,
  selection: SmartSearchSelection,
  rawQuery: string,
  context: RecognizedContext
): number => {
  const normalizedQuery = normalizeText(rawQuery);
  const queryTokens = tokenize(rawQuery);
  const nonGenericQueryTokens = getNonGenericTokens(rawQuery);
  const genericOnly = isGlobalIntentOnlyQuery(rawQuery);
  if (!normalizedQuery || genericOnly) return 0;

  const {
    normalizedTexts,
    allEntryTokens,
    titleTokens,
    inputLabelTokens,
    aliasTokens,
    objectTokens,
    categoryTokens,
    domainTokens,
  } = getEntrySearchIndex(entry, selection);
  const queryTokenMatches = queryTokens.filter((token, index) =>
    findMatchingCandidateToken(
      token,
      allEntryTokens,
      index === queryTokens.length - 1 || token.length >= 3
    )
  );
  const nonGenericMatches = nonGenericQueryTokens.filter((token, index) =>
    findMatchingCandidateToken(
      token,
      allEntryTokens,
      index === nonGenericQueryTokens.length - 1 || token.length >= 3
    )
  );
  const queryHasObjectOrCategory =
    Boolean(context.object && selection.objectId === context.object.id) ||
    Boolean(context.category && selection.categoryId === context.category.id);

  if (
    nonGenericQueryTokens.length > 0 &&
    nonGenericMatches.length !== nonGenericQueryTokens.length
  ) {
    return 0;
  }

  let score = 0;

  for (const text of normalizedTexts) {
    if (text === normalizedQuery) {
      score = Math.max(score, 1000);
    } else if (text.startsWith(normalizedQuery) && normalizedQuery.length >= 3) {
      score = Math.max(score, 900);
    } else if (text.includes(normalizedQuery) && normalizedQuery.length >= 4) {
      score = Math.max(score, 520);
    } else if (normalizedQuery.includes(text) && tokenize(text).length > 1) {
      score = Math.max(score, 650);
    }
  }

  if (queryTokenMatches.length === queryTokens.length && queryTokens.length > 0) {
    score += 150 + queryTokenMatches.length * 28;
  }

  const titleMatches = queryTokens.filter((token, index) =>
    findMatchingCandidateToken(
      token,
      titleTokens,
      index === queryTokens.length - 1 || token.length >= 3
    )
  );
  if (titleMatches.length === queryTokens.length && queryTokens.length > 0) {
    score += 260;
  }

  if (nonGenericMatches.length > 0) {
    score += nonGenericMatches.length * 60;
  }

  const objectMatches = nonGenericQueryTokens.filter((token, index) =>
    findMatchingCandidateToken(
      token,
      objectTokens,
      index === nonGenericQueryTokens.length - 1 || token.length >= 3
    )
  );
  const aliasMatches = nonGenericQueryTokens.filter((token, index) =>
    findMatchingCandidateToken(
      token,
      aliasTokens,
      index === nonGenericQueryTokens.length - 1 || token.length >= 3
    )
  );
  const categoryMatches = nonGenericQueryTokens.filter((token, index) =>
    findMatchingCandidateToken(
      token,
      categoryTokens,
      index === nonGenericQueryTokens.length - 1 || token.length >= 3
    )
  );
  const domainMatches = nonGenericQueryTokens.filter((token, index) =>
    findMatchingCandidateToken(
      token,
      domainTokens,
      index === nonGenericQueryTokens.length - 1 || token.length >= 3
    )
  );
  const inputLabelMatches = nonGenericQueryTokens.filter((token, index) =>
    findMatchingCandidateToken(
      token,
      inputLabelTokens,
      index === nonGenericQueryTokens.length - 1 || token.length >= 3
    )
  );

  if (objectMatches.length > 0) {
    score += objectMatches.length * 92;
  }
  if (aliasMatches.length > 0) {
    score += aliasMatches.length * 64;
  }
  if (inputLabelMatches.length > 0) {
    score += inputLabelMatches.length * 52;
  }
  if (categoryMatches.length > 0) {
    score += categoryMatches.length * 28;
  }
  if (domainMatches.length > 0) {
    score += domainMatches.length * 18;
  }

  if (queryHasObjectOrCategory && context.intent && selection.intent === context.intent) {
    score += 170;
  } else if (queryHasObjectOrCategory) {
    score += 80;
  } else if (
    context.intent &&
    selection.intent === context.intent &&
    nonGenericMatches.length > 0
  ) {
    score += 70;
  }

  if (context.intent && selection.intent && selection.intent !== context.intent) {
    score -= 90;
  }

  if (queryTokens.length > 0 && nonGenericMatches.length === 0 && !queryHasObjectOrCategory) {
    return 0;
  }

  score += Number(entry.matchWeight || 0);
  score += getSpecificityBoost(entry.specificity);
  score += getCommonnessBoost(entry.commonness);
  score -= getFallbackPenalty(entry.fallbackLevel);
  if (entry.specificity === 'category') {
    score -= 40;
  }

  return score;
};

const toScoredEntries = (rawQuery: string, context: RecognizedContext): ScoredEntry[] =>
  publicSearchEntries
    .map(({ raw, selection }) => ({
      selection,
      score: scoreEntry(raw, selection, rawQuery, context),
      fallbackEntryIds: toStringList(raw.fallbackEntryIds),
      fallbackObjectId: toText(raw.fallbackObjectId) || undefined,
      fallbackCategoryId: toText(raw.fallbackCategoryId) || undefined,
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

const findObjectDiagnosticEntry = (objectId: string): SmartSearchSelection | null =>
  publicSearchEntries.find(
    (entry) => entry.selection.objectId === objectId && entry.selection.intent === 'D'
  )?.selection || null;

const findObjectRepairEntry = (objectId: string): SmartSearchSelection | null =>
  publicSearchEntries.find(
    (entry) =>
      entry.selection.objectId === objectId &&
      entry.selection.intent === 'N' &&
      entry.raw.specificity === 'object'
  )?.selection || null;

const getIntentOnlyEntrySuggestions = (
  intent?: ServiceCapabilityIntent
): SmartSearchSelection[] => {
  if (intent !== 'D') return [];

  const diagnosticEntries = publicSearchEntries.filter(
    (entry) => entry.selection.intent === 'D' && entry.selection.objectId
  );
  const byObjectId = new Map<string, SmartSearchSelection>();

  for (const entry of diagnosticEntries) {
    const objectId = entry.selection.objectId;
    if (!objectId || byObjectId.has(objectId)) continue;
    byObjectId.set(objectId, entry.selection);
  }

  const ordered = DIAGNOSTIC_INTENT_ONLY_OBJECT_ORDER.map((objectId) =>
    byObjectId.get(objectId)
  ).filter((entry): entry is SmartSearchSelection => Boolean(entry));
  const orderedIds = new Set(ordered.map((entry) => entry.id));
  const remaining = diagnosticEntries
    .filter((entry) => !orderedIds.has(entry.selection.id))
    .sort((a, b) => {
      const commonnessDiff =
        getCommonnessBoost(b.raw.commonness) - getCommonnessBoost(a.raw.commonness);
      if (commonnessDiff !== 0) return commonnessDiff;
      return Number(b.raw.matchWeight || 0) - Number(a.raw.matchWeight || 0);
    })
    .map((entry) => entry.selection);

  return ordered.concat(remaining).slice(0, SUGGESTION_LIMIT - 1);
};

const objectToSelection = (
  object: PublicSmartSearchObject,
  intent?: ServiceCapabilityIntent
): SmartSearchSelection => {
  const category = categoryById.get(object.categoryId);
  const title = object.pluralLabel || object.label;
  return {
    id: object.id,
    type: 'object',
    matchType: 'object',
    suggestionTitle: title,
    suggestionMeta: `${intent ? `${INTENT_LABELS[intent]} · ` : ''}Sprzęt · ${
      category?.label || 'Kategoria'
    }`,
    inputSelectedLabel: title,
    intent,
    domainId: object.domainId,
    categoryId: object.categoryId,
    objectId: object.id,
  };
};

const categoryToSelection = (
  category: PublicSmartSearchCategory,
  intent?: ServiceCapabilityIntent
): SmartSearchSelection => ({
  id: category.id,
  type: 'category',
  matchType: 'category',
  suggestionTitle: category.label,
  suggestionMeta: `${intent ? `${INTENT_LABELS[intent]} · ` : ''}Kategoria`,
  inputSelectedLabel: category.label,
  intent,
  domainId: category.domainId,
  categoryId: category.id,
});

export const createReportMissingSuggestion = (rawQuery = ''): SmartSearchSelection => ({
  id: 'reportMissingService',
  type: 'reportMissing',
  matchType: 'reportMissing',
  suggestionTitle: 'Brakuje pozycji?',
  suggestionMeta: 'Zgłoś brakującą usługę',
  inputSelectedLabel: rawQuery.trim() ? `Brakuje: ${rawQuery.trim()}` : 'Zgłoś brakującą usługę',
});

const providerToSelection = (service: ServiceLocationInfo): SmartSearchSelection => {
  const city = String(service.address || '')
    .split(',')
    .pop()
    ?.trim();
  return {
    id: service.uid,
    type: 'provider',
    matchType: 'provider',
    suggestionTitle: service.serviceName || 'Specjalista',
    suggestionMeta: city ? `Specjalista · ${city}` : 'Specjalista',
    inputSelectedLabel: service.serviceName || 'Specjalista',
    providerId: service.uid,
  };
};

const appendUnique = (
  suggestions: SmartSearchSelection[],
  suggestion: SmartSearchSelection | null | undefined
) => {
  if (!suggestion) return;
  if (
    suggestions.some(
      (current) =>
        (current.matchType === suggestion.matchType && current.id === suggestion.id) ||
        (current.suggestionTitle === suggestion.suggestionTitle &&
          current.suggestionMeta === suggestion.suggestionMeta)
    )
  ) {
    return;
  }
  suggestions.push(suggestion);
};

const getRuleBackedFallbacks = (
  rawQuery: string
): {
  entryIds: string[];
  objectId?: string;
  categoryId?: string;
} => {
  const normalizedQuery = normalizeText(rawQuery);
  const matchedRule = fallbackRules.find((rule) =>
    rule.aliases.some(
      (alias) => alias && (normalizedQuery.includes(alias) || alias.includes(normalizedQuery))
    )
  );
  if (!matchedRule) return { entryIds: [] };
  return {
    entryIds: [matchedRule.preferredEntryId, matchedRule.fallbackEntryId].filter(Boolean),
    objectId: matchedRule.fallbackObjectId || undefined,
    categoryId: matchedRule.fallbackCategoryId || undefined,
  };
};

export const getSmartSearchSuggestions = (
  rawQuery: string,
  services: ServiceLocationInfo[] = []
): SmartSearchSelection[] => {
  const query = rawQuery.trim();
  if (!query) return [];

  const normalizedQuery = normalizeText(query);
  const context = getContext(query);
  const scoredEntries = toScoredEntries(query, context);
  const ruleFallbacks = getRuleBackedFallbacks(query);
  const suggestions: SmartSearchSelection[] = [];
  const intentOnlySuggestions = isGlobalIntentOnlyQuery(query)
    ? getIntentOnlyEntrySuggestions(context.intent)
    : [];

  const providerSuggestions =
    normalizedQuery.length >= 2
      ? services
          .filter((service) => service.isTestData !== true)
          .filter((service) => normalizeText(service.serviceName).includes(normalizedQuery))
          .sort((a, b) => {
            const aStarts = normalizeText(a.serviceName).startsWith(normalizedQuery) ? 0 : 1;
            const bStarts = normalizeText(b.serviceName).startsWith(normalizedQuery) ? 0 : 1;
            if (aStarts !== bStarts) return aStarts - bStarts;
            return String(a.serviceName || '').localeCompare(String(b.serviceName || ''), 'pl');
          })
          .slice(0, 2)
          .map(providerToSelection)
      : [];

  providerSuggestions.forEach((suggestion) => appendUnique(suggestions, suggestion));
  intentOnlySuggestions.forEach((suggestion) => appendUnique(suggestions, suggestion));
  scoredEntries.slice(0, 3).forEach((entry) => appendUnique(suggestions, entry.selection));

  [
    ...ruleFallbacks.entryIds,
    ...scoredEntries.slice(0, 2).flatMap((entry) => entry.fallbackEntryIds),
  ].forEach((entryId) => appendUnique(suggestions, entryById.get(entryId)?.selection));

  const fallbackObject =
    context.object ||
    objectById.get(ruleFallbacks.objectId || '') ||
    objectById.get(scoredEntries[0]?.fallbackObjectId || '') ||
    objectById.get(scoredEntries[0]?.selection.objectId || '');
  const fallbackCategory =
    context.category ||
    categoryById.get(ruleFallbacks.categoryId || '') ||
    categoryById.get(scoredEntries[0]?.fallbackCategoryId || '') ||
    (fallbackObject ? categoryById.get(fallbackObject.categoryId) : undefined);

  if (fallbackObject) {
    appendUnique(suggestions, findObjectDiagnosticEntry(fallbackObject.id));
    appendUnique(suggestions, findObjectRepairEntry(fallbackObject.id));
    appendUnique(suggestions, objectToSelection(fallbackObject, context.intent));
  }
  if (fallbackCategory) {
    appendUnique(suggestions, categoryToSelection(fallbackCategory, context.intent));
  }

  const reportMissing = createReportMissingSuggestion(query);
  const withoutReportMissing = suggestions.filter(
    (suggestion) => suggestion.type !== 'reportMissing'
  );
  return withoutReportMissing.slice(0, SUGGESTION_LIMIT - 1).concat(reportMissing);
};

export const getSmartSearchSelectionByParams = (
  params: Pick<URLSearchParams, 'get'>
): SmartSearchSelection | null => {
  const matchType = params.get('matchType') as SmartSearchMatchType | null;
  const smartEntryId = toText(params.get('smartEntryId'));
  const queryLabel = toText(params.get('q'));
  const intent = isIntent(params.get('intent'))
    ? (params.get('intent') as ServiceCapabilityIntent)
    : undefined;
  const domainId = toText(params.get('domainId'));
  const categoryId = toText(params.get('categoryId'));
  const objectId = toText(params.get('objectId'));

  if (!matchType || !smartEntryId) return null;

  if (matchType === 'entry') {
    const selection = entryById.get(smartEntryId)?.selection;
    if (!selection) return null;
    return {
      ...selection,
      inputSelectedLabel: queryLabel || selection.inputSelectedLabel,
      intent: intent || selection.intent,
    };
  }

  if (matchType === 'object') {
    const object = objectById.get(objectId || smartEntryId);
    return object
      ? {
          ...objectToSelection(object, intent),
          inputSelectedLabel: queryLabel || object.pluralLabel || object.label,
        }
      : null;
  }

  if (matchType === 'category') {
    const category = categoryById.get(categoryId || smartEntryId);
    return category
      ? {
          ...categoryToSelection(category, intent),
          inputSelectedLabel: queryLabel || category.label,
        }
      : null;
  }

  if (matchType === 'provider') {
    return {
      id: smartEntryId,
      type: 'provider',
      matchType: 'provider',
      suggestionTitle: queryLabel || 'Wybrany specjalista',
      suggestionMeta: 'Specjalista',
      inputSelectedLabel: queryLabel || 'Wybrany specjalista',
      providerId: smartEntryId,
    };
  }

  if (matchType === 'reportMissing') {
    return createReportMissingSuggestion(queryLabel);
  }

  if (domainId || categoryId || objectId) {
    return {
      id: smartEntryId,
      type: 'category',
      matchType,
      suggestionTitle: queryLabel || 'Wybrane dopasowanie',
      suggestionMeta: 'Dopasowanie',
      inputSelectedLabel: queryLabel || 'Wybrane dopasowanie',
      intent,
      domainId: domainId || undefined,
      categoryId: categoryId || undefined,
      objectId: objectId || undefined,
    };
  }

  return null;
};
